// src/components/BrowserTimelapse.js
// Browser-based timelapse generation component
// Downloads files via presigned URLs and processes locally using Web Worker
// Encodes video incrementally using mp4-muxer - frames are encoded as they stream in from the worker
// This streaming approach prevents memory exhaustion for large file sets (9000+ frames)

/* global VideoEncoder, VideoFrame */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Typography,
    Alert,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    Movie as MovieIcon,
    Close as CloseIcon,
    Download as DownloadIcon,
    Warning as WarningIcon,
    VideoLibrary as VideoLibraryIcon
} from '@mui/icons-material';

// API base URL
const API_BASE = process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : 'https://drive.suprsketch.com';

/**
 * Initialize video encoder and muxer for incremental frame encoding
 * Returns an encoder state object used by encodeSingleFrame and finalizeVideo
 */
const initVideoEncoder = async (width, height, frameRate, quality) => {
    if (typeof VideoEncoder === 'undefined') {
        throw new Error('WebCodecs API not supported. Please use Chrome or Edge browser.');
    }

    const MAX_WIDTH = 3840;
    const MAX_HEIGHT = 2160;

    const pixels = width * height;
    const pixels1080p = 1920 * 1080;
    const pixels4K = 3840 * 2160;

    const baseBitrates = {
        low: 2000000,
        medium: 5000000,
        high: 10000000,
        lossless: 20000000
    };

    let bitrateMultiplier = 1;
    if (pixels > pixels4K * 0.5) {
        bitrateMultiplier = 4;
    } else if (pixels > pixels1080p * 1.5) {
        bitrateMultiplier = 2;
    }

    const bitrate = (baseBitrates[quality] || baseBitrates.high) * bitrateMultiplier;
    console.log(`Resolution: ${width}x${height}, Bitrate multiplier: ${bitrateMultiplier}x, Final bitrate: ${(bitrate / 1000000).toFixed(1)} Mbps`);

    let outputWidth = width;
    let outputHeight = height;

    if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const scale = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        outputWidth = Math.floor(width * scale);
        outputHeight = Math.floor(height * scale);
        console.log(`Scaling down from ${width}x${height} to ${outputWidth}x${outputHeight}`);
    }

    const evenWidth = outputWidth % 2 === 0 ? outputWidth : outputWidth - 1;
    const evenHeight = outputHeight % 2 === 0 ? outputHeight : outputHeight - 1;

    console.log('Encoding dimensions:', evenWidth, 'x', evenHeight, 'frameRate:', frameRate, 'bitrate:', bitrate);

    const canvas = document.createElement('canvas');
    canvas.width = evenWidth;
    canvas.height = evenHeight;
    const ctx = canvas.getContext('2d');

    const muxer = new Muxer({
        target: new ArrayBufferTarget(),
        video: {
            codec: 'avc',
            width: evenWidth,
            height: evenHeight
        },
        fastStart: 'in-memory'
    });

    const codecsToTry = [
        'avc1.640034', 'avc1.640033', 'avc1.640032',
        'avc1.64002A', 'avc1.640029', 'avc1.640028',
        'avc1.4D0034', 'avc1.4D0033', 'avc1.4D0032',
        'avc1.4D0029', 'avc1.4D0028',
    ];

    let encoderConfig = null;

    for (const codec of codecsToTry) {
        const testConfig = {
            codec: codec,
            width: evenWidth,
            height: evenHeight,
            bitrate: bitrate,
            framerate: frameRate,
            avc: { format: 'avc' }
        };

        try {
            const support = await VideoEncoder.isConfigSupported(testConfig);
            if (support.supported) {
                encoderConfig = support.config || testConfig;
                console.log('Using codec:', codec);
                break;
            }
        } catch (e) {
            // Try next codec
        }
    }

    if (!encoderConfig) {
        throw new Error(`H.264 encoding not supported. Dimensions: ${evenWidth}x${evenHeight}. Please use Chrome or Edge.`);
    }

    // Shared error holder - captures async encoder errors so they can be checked synchronously
    const errorHolder = { error: null };

    // Cache decoderConfig from the first chunk that provides it.
    // Some browsers (e.g. Firefox) don't include decoderConfig in output metadata,
    // and even Chrome may only provide it on the first keyframe.
    let cachedDecoderConfig = null;

    const encoder = new VideoEncoder({
        output: (chunk, meta) => {
            if (meta?.decoderConfig) {
                cachedDecoderConfig = meta.decoderConfig;
            }
            // Provide cached decoderConfig if current meta doesn't have it
            const safeMeta = meta?.decoderConfig ? meta
                : cachedDecoderConfig ? { ...(meta || {}), decoderConfig: cachedDecoderConfig }
                : meta;
            muxer.addVideoChunk(chunk, safeMeta);
        },
        error: (error) => {
            console.error('Encoder error:', error);
            errorHolder.error = error;
        }
    });

    encoder.configure(encoderConfig);

    return {
        encoder,
        muxer,
        canvas,
        ctx,
        evenWidth,
        evenHeight,
        frameDurationMicros: 1000000 / frameRate,
        frameIndex: 0,
        errorHolder
    };
};

/**
 * Encode a single frame blob into the video
 * Called incrementally as frames arrive from the worker
 */
const encodeSingleFrame = async (state, blob) => {
    const { canvas, ctx, encoder, evenWidth, evenHeight, frameDurationMicros, errorHolder } = state;

    // Guard: check if encoder has been closed or errored
    if (encoder.state === 'closed') {
        const errMsg = errorHolder?.error?.message || 'Encoder closed unexpectedly';
        throw new Error(`Encoder closed: ${errMsg}`);
    }

    const img = await new Promise((resolve) => {
        const image = new Image();
        const url = URL.createObjectURL(blob);
        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
        };
        image.src = url;
    });

    if (!img) return;

    ctx.drawImage(img, 0, 0, evenWidth, evenHeight);

    const timestamp = state.frameIndex * frameDurationMicros;
    const frame = new VideoFrame(canvas, {
        timestamp: timestamp,
        duration: frameDurationMicros
    });

    encoder.encode(frame, { keyFrame: state.frameIndex % 30 === 0 });
    frame.close();

    state.frameIndex++;

    // Yield to prevent blocking UI
    if (state.frameIndex % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
    }
};

/**
 * Add freeze frames and finalize the video
 * Returns the final video blob
 */
const finalizeVideo = async (state, freezeDuration, frameRate, onProgress) => {
    const { canvas, encoder, muxer, frameDurationMicros } = state;
    const totalFrames = state.frameIndex;

    if (freezeDuration > 0 && totalFrames > 0) {
        const freezeFrameCount = Math.round(freezeDuration * frameRate);
        const totalWithFreeze = totalFrames + freezeFrameCount;

        console.log(`Adding ${freezeFrameCount} freeze frames (${freezeDuration}s at ${frameRate}fps)`);

        for (let i = 0; i < freezeFrameCount; i++) {
            // Guard: stop if encoder was closed by an error
            if (encoder.state === 'closed') {
                const errMsg = state.errorHolder?.error?.message || 'Encoder closed unexpectedly';
                throw new Error(`Encoder closed during freeze frames: ${errMsg}`);
            }

            const frameIndex = totalFrames + i;
            const timestamp = frameIndex * frameDurationMicros;

            const frame = new VideoFrame(canvas, {
                timestamp: timestamp,
                duration: frameDurationMicros
            });

            encoder.encode(frame, { keyFrame: frameIndex % 30 === 0 });
            frame.close();

            if (i % 10 === 0) {
                if (onProgress) {
                    onProgress(totalFrames + i, totalWithFreeze, 'Adding freeze frames...');
                }
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }

        if (onProgress) {
            onProgress(totalWithFreeze, totalWithFreeze, 'Finalizing...');
        }
    }

    await encoder.flush();
    encoder.close();

    try {
        muxer.finalize();
    } catch (muxerError) {
        // mp4-muxer crashes if VideoEncoder never provided decoderConfig metadata
        // (e.g., Firefox's WebCodecs implementation omits it)
        throw new Error(
            'Video encoding failed: your browser did not provide required codec metadata. '
            + 'Please use Google Chrome or Microsoft Edge to generate timelapses.'
        );
    }

    const buffer = muxer.target.buffer;
    return new Blob([buffer], { type: 'video/mp4' });
};

/**
 * Browser Timelapse Generation Component
 */
const BrowserTimelapse = ({
    folderPath,
    bucketType = 'live',
    onComplete,
    onViewInVideoBucket,
    disabled = false,
    fps = 30,
    frameSelection = 'include-strokes',
    freezeDuration = 3
}) => {
    // State
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [stage, setStage] = useState('');
    const [message, setMessage] = useState('');
    const [videoBlob, setVideoBlob] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [frameRate, setFrameRate] = useState(fps);
    const [quality, setQuality] = useState('high');
    const [fileCount, setFileCount] = useState(0);
    const [uploadedVideo, setUploadedVideo] = useState(null);

    // Sync frameRate with fps prop when it changes
    useEffect(() => {
        setFrameRate(fps);
    }, [fps]);

    // Refs
    const workerRef = useRef(null);
    const videoUrlRef = useRef(null);
    // Incremental encoding refs
    const encoderStateRef = useRef(null);
    const frameQueueRef = useRef([]);
    const processingRef = useRef(false);
    const workerDoneMetaRef = useRef(null);

    /**
     * Handle worker completion - finalize video and upload to S3
     */
    const handleWorkerDone = useCallback(async (workerMetadata) => {
        try {
            if (!encoderStateRef.current) {
                throw new Error('No frames were encoded');
            }

            setStage('encoding');
            setMessage('Finalizing video...');
            setProgress(90);

            const videoBlobResult = await finalizeVideo(
                encoderStateRef.current,
                freezeDuration,
                workerMetadata.frameRate,
                (current, total, msg) => {
                    const p = 90 + Math.round((current / total) * 5);
                    setProgress(p);
                    setMessage(msg);
                }
            );

            const state = encoderStateRef.current;
            const originalWidth = workerMetadata.width;
            const originalHeight = workerMetadata.height;
            const finalWidth = state.evenWidth;
            const finalHeight = state.evenHeight;

            // Calculate final metadata
            const finalMetadata = {
                ...workerMetadata,
                width: finalWidth,
                height: finalHeight,
                originalWidth,
                originalHeight,
                wasDownscaled: finalWidth !== originalWidth || finalHeight !== originalHeight,
                fileSize: videoBlobResult.size,
                mimeType: videoBlobResult.type
            };

            setVideoBlob(videoBlobResult);
            setMetadata(finalMetadata);
            encoderStateRef.current = null;

            // Upload to S3 automatically after encoding
            setStage('uploading');
            setMessage('Uploading to cloud...');

            try {
                // Get presigned upload URL
                const uploadUrlResponse = await fetch(`${API_BASE}/api/browser-timelapse/upload-url`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({ folderPath })
                });

                if (!uploadUrlResponse.ok) {
                    const errorData = await uploadUrlResponse.json();
                    throw new Error(errorData.error || `Failed to get upload URL: ${uploadUrlResponse.status}`);
                }

                const { presignedUrl, videoKey, bucket } = await uploadUrlResponse.json();

                // Upload video blob directly to S3
                setMessage('Uploading video to S3...');
                const uploadResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'video/mp4'
                    },
                    body: videoBlobResult
                });

                if (!uploadResponse.ok) {
                    throw new Error(`Upload failed: ${uploadResponse.status}`);
                }

                // Store uploaded video info for navigation
                setUploadedVideo({ key: videoKey, bucket });

                console.log('Video uploaded successfully:', { videoKey, bucket });

                setLoading(false);
                setStage('complete');
                setProgress(100);

                if (finalMetadata.wasDownscaled) {
                    setMessage(`Timelapse complete! (Downscaled to ${finalWidth}x${finalHeight} for compatibility)`);
                } else {
                    setMessage('Timelapse complete!');
                }

            } catch (uploadErr) {
                console.error('Upload error:', uploadErr);
                // Still mark as complete but show upload failed - user can still download locally
                setLoading(false);
                setStage('complete');
                setProgress(100);
                setMessage(`Timelapse complete! (Upload failed: ${uploadErr.message})`);
                setUploadedVideo(null);
            }

        } catch (err) {
            console.error('Encoding error:', err);
            setError(`Encoding failed: ${err.message}`);
            setLoading(false);
            encoderStateRef.current = null;
        }
    }, [freezeDuration, folderPath]);

    /**
     * Initialize Web Worker with incremental frame encoding
     * Frames are encoded as they stream in from the worker instead of accumulating all in memory
     */
    const initWorker = useCallback(() => {
        if (workerRef.current) {
            workerRef.current.terminate();
        }

        // Reset encoder state for new generation
        encoderStateRef.current = null;
        frameQueueRef.current = [];
        processingRef.current = false;
        workerDoneMetaRef.current = null;

        /**
         * Process queued frames incrementally
         * Ensures frames are encoded in order and handles encoder initialization on first frame
         */
        const processQueue = async () => {
            if (processingRef.current) return;
            processingRef.current = true;

            try {
                while (frameQueueRef.current.length > 0 || workerDoneMetaRef.current) {
                    // Encode all queued frames
                    while (frameQueueRef.current.length > 0) {
                        const { blob, width, height } = frameQueueRef.current.shift();

                        // Initialize encoder lazily on first frame
                        if (!encoderStateRef.current) {
                            try {
                                encoderStateRef.current = await initVideoEncoder(
                                    width, height, frameRate, quality
                                );
                            } catch (initError) {
                                // Fallback to 1080p if original resolution fails
                                if (width > 1920 || height > 1080) {
                                    const aspectRatio = width / height;
                                    let fallbackWidth, fallbackHeight;

                                    if (aspectRatio > 16 / 9) {
                                        fallbackWidth = 1920;
                                        fallbackHeight = Math.floor(1920 / aspectRatio);
                                    } else {
                                        fallbackHeight = 1080;
                                        fallbackWidth = Math.floor(1080 * aspectRatio);
                                    }

                                    fallbackWidth = fallbackWidth % 2 === 0 ? fallbackWidth : fallbackWidth - 1;
                                    fallbackHeight = fallbackHeight % 2 === 0 ? fallbackHeight : fallbackHeight - 1;

                                    console.log(`Falling back from ${width}x${height} to ${fallbackWidth}x${fallbackHeight}`);
                                    setMessage(`Retrying encoder at ${fallbackWidth}x${fallbackHeight}...`);

                                    encoderStateRef.current = await initVideoEncoder(
                                        fallbackWidth, fallbackHeight, frameRate, quality
                                    );
                                } else {
                                    throw initError;
                                }
                            }
                        }

                        // Check for async encoder error before encoding next frame
                        if (encoderStateRef.current.errorHolder?.error) {
                            throw new Error(`Encoder error: ${encoderStateRef.current.errorHolder.error.message}`);
                        }

                        await encodeSingleFrame(encoderStateRef.current, blob);
                    }

                    // If worker is done and queue is empty, finalize
                    if (workerDoneMetaRef.current && frameQueueRef.current.length === 0) {
                        const meta = workerDoneMetaRef.current;
                        workerDoneMetaRef.current = null;
                        await handleWorkerDone(meta);
                        break;
                    }

                    // Queue empty but worker not done yet - exit loop
                    // Will be called again when next frame arrives
                    break;
                }
            } catch (err) {
                console.error('Frame encoding error:', err);
                setError(`Encoding failed: ${err.message}`);
                setLoading(false);
                encoderStateRef.current = null;
                // Terminate worker to stop more frames from arriving
                if (workerRef.current) {
                    workerRef.current.terminate();
                    workerRef.current = null;
                }
            } finally {
                processingRef.current = false;
            }
        };

        workerRef.current = new Worker(
            new URL('../workers/timelapseWorker.js', import.meta.url)
        );

        workerRef.current.onmessage = (event) => {
            const { type, ...data } = event.data;

            switch (type) {
                case 'ready':
                    console.log('Timelapse worker ready');
                    break;

                case 'progress':
                    setStage(data.stage);
                    setProgress(data.progress);
                    setMessage(data.message);
                    break;

                case 'frame':
                    // Queue frame for incremental encoding
                    frameQueueRef.current.push({
                        blob: data.blob,
                        width: data.width,
                        height: data.height
                    });
                    processQueue();
                    break;

                case 'done':
                    // Worker finished processing all files - terminate it
                    if (workerRef.current) {
                        workerRef.current.terminate();
                        workerRef.current = null;
                    }
                    // Signal that all frames have been sent
                    workerDoneMetaRef.current = data.metadata;
                    processQueue();
                    break;

                case 'error':
                    // Terminate worker on error
                    if (workerRef.current) {
                        workerRef.current.terminate();
                        workerRef.current = null;
                    }
                    setError(data.message);
                    setLoading(false);
                    break;

                default:
                    console.warn('Unknown worker message type:', type);
            }
        };

        workerRef.current.onerror = (error) => {
            console.error('Worker error:', error);
            // Terminate worker on error
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
            setError(`Worker error: ${error.message}`);
            setLoading(false);
        };
    }, [handleWorkerDone, frameRate, quality]);

    /**
     * Clean up worker and resources
     */
    const cleanup = useCallback(() => {
        if (workerRef.current) {
            workerRef.current.terminate();
            workerRef.current = null;
        }
        if (videoUrlRef.current) {
            URL.revokeObjectURL(videoUrlRef.current);
            videoUrlRef.current = null;
        }
        encoderStateRef.current = null;
        frameQueueRef.current = [];
        processingRef.current = false;
        workerDoneMetaRef.current = null;
    }, []);

    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    const handleOpen = () => {
        setOpen(true);
        setError(null);
        setProgress(0);
        setStage('');
        setMessage('');
        setVideoBlob(null);
        setMetadata(null);
        setUploadedVideo(null);
    };

    const handleClose = () => {
        if (loading) {
            if (workerRef.current) {
                workerRef.current.postMessage({ type: 'cancel' });
            }
        }
        setOpen(false);
        cleanup();
    };

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        setProgress(0);
        setStage('preparing');
        setMessage('Fetching file list...');

        try {
            const response = await fetch(`${API_BASE}/api/browser-timelapse/presign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    folderPath,
                    bucketType
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error: ${response.status}`);
            }

            const { files, metadata: fileMeta } = await response.json();

            if (files.length === 0) {
                throw new Error('No timelapse files found in this folder');
            }

            // Filter files based on frameSelection
            let filteredFiles = files;
            if (frameSelection === 'snapshots-only') {
                // Only include snapshots (.json, .suprs) - exclude deltas (.supr)
                filteredFiles = files.filter(f => f.type === 'snapshot');
            }

            if (filteredFiles.length === 0) {
                throw new Error('No files match the selected frame selection');
            }

            const snapshotCount = filteredFiles.filter(f => f.type === 'snapshot').length;
            const deltaCount = filteredFiles.filter(f => f.type === 'delta').length;
            setFileCount(filteredFiles.length);
            setMessage(`Processing ${filteredFiles.length} files (${snapshotCount} snapshots, ${deltaCount} deltas)`);

            initWorker();
            await new Promise(resolve => setTimeout(resolve, 100));

            workerRef.current.postMessage({
                type: 'generate',
                data: {
                    files: filteredFiles,
                    options: {
                        frameRate
                    }
                }
            });

        } catch (err) {
            console.error('Timelapse generation error:', err);
            setError(err.message);
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (!videoBlob) return;

        const url = URL.createObjectURL(videoBlob);
        videoUrlRef.current = url;

        // Determine extension from mime type
        const ext = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';

        const a = document.createElement('a');
        a.href = url;
        a.download = `timelapse-${Date.now()}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        if (onComplete) {
            onComplete({ videoBlob, metadata });
        }
    };

    const handleViewInVideoBucket = () => {
        if (!uploadedVideo || !onViewInVideoBucket) return;

        // Close the modal and navigate to video bucket
        handleClose();
        onViewInVideoBucket(uploadedVideo);
    };

    const getStageLabel = () => {
        switch (stage) {
            case 'preparing': return 'Preparing';
            case 'download': return 'Processing';
            case 'encoding': return 'Encoding';
            case 'uploading': return 'Uploading';
            case 'complete': return 'Complete';
            case 'cancelled': return 'Cancelled';
            default: return 'Ready';
        }
    };

    const checkCompatibility = () => {
        const hasWorker = typeof Worker !== 'undefined';
        const hasOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';
        const hasVideoEncoder = typeof VideoEncoder !== 'undefined';
        const warnings = [];

        // Firefox's VideoEncoder does not provide decoderConfig metadata,
        // which mp4-muxer requires to create a valid MP4 file
        const isFirefox = navigator.userAgent.includes('Firefox');
        if (isFirefox) {
            warnings.push('Firefox is not supported for browser timelapse. Please use Chrome or Edge.');
        }

        return {
            supported: hasWorker && hasOffscreenCanvas && hasVideoEncoder && !isFirefox,
            warnings
        };
    };

    const compatibility = checkCompatibility();

    // Start generation immediately when button is clicked
    const handleStartGeneration = () => {
        setOpen(true);
        // Small delay to let dialog render, then start
        setTimeout(() => {
            handleGenerate();
        }, 100);
    };

    return (
        <>
            <Tooltip title={!compatibility.supported ? 'Browser not supported' : 'Generate timelapse in browser'}>
                <span>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleStartGeneration}
                        disabled={disabled || !folderPath || !compatibility.supported}
                        sx={{
                            px: 3,
                            borderRadius: 1.5,
                            boxShadow: 2,
                            '&:hover': {
                                boxShadow: 3,
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s',
                        }}
                    >
                        Generate Timelapse
                    </Button>
                </span>
            </Tooltip>

            <Dialog
                open={open}
                onClose={(event, reason) => {
                    // Prevent closing by clicking backdrop or pressing escape during loading
                    if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
                        return;
                    }
                    handleClose();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MovieIcon />
                        Browser Timelapse Generator
                    </Box>
                    <IconButton onClick={handleClose} disabled={loading}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    {compatibility.warnings.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                            {compatibility.warnings.join('. ')}
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                <Typography variant="subtitle2">
                                    {getStageLabel()}: {message}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{ height: 8, borderRadius: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {progress}% complete
                                {fileCount > 0 && ` - Processing ${fileCount} files`}
                            </Typography>
                        </Box>
                    )}

                    {videoBlob && metadata && (
                        <Box sx={{ mb: 2 }}>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Timelapse generated successfully!
                                {metadata.wasDownscaled && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                        Note: Downscaled for browser compatibility
                                    </Typography>
                                )}
                            </Alert>
                            <Typography variant="body2">
                                <strong>Frames:</strong> {metadata.totalFrames}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Resolution:</strong> {metadata.width} x {metadata.height}
                                {metadata.wasDownscaled && (
                                    <Typography component="span" variant="caption" color="text.secondary">
                                        {' '}(original: {metadata.originalWidth} x {metadata.originalHeight})
                                    </Typography>
                                )}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Duration:</strong> {(metadata.videoDuration || 0).toFixed(1)}s
                            </Typography>
                            <Typography variant="body2">
                                <strong>File size:</strong> {(metadata.fileSize / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                            <Typography variant="body2">
                                <strong>Format:</strong> {metadata.mimeType || 'video'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Processing time:</strong> {((metadata.processingDuration || 0) / 1000).toFixed(1)}s
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    {loading && (
                        <Button onClick={handleClose} color="error">
                            Cancel Processing
                        </Button>
                    )}

                    {!loading && error && (
                        <Button onClick={handleClose}>Close</Button>
                    )}

                    {videoBlob && (
                        <>
                            <Button onClick={handleClose}>Close</Button>
                            <Button
                                variant="outlined"
                                onClick={handleDownload}
                                startIcon={<DownloadIcon />}
                            >
                                Download Video
                            </Button>
                            {uploadedVideo && onViewInVideoBucket && (
                                <Button
                                    variant="contained"
                                    onClick={handleViewInVideoBucket}
                                    startIcon={<VideoLibraryIcon />}
                                >
                                    View in Video Bucket
                                </Button>
                            )}
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BrowserTimelapse;
